import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import SalarySlipForm from "./components/SalarySlipForm";
import { salarySlipService } from "../../services";
import { makeEmptySalarySlipFormValues } from "./utils";
import PageSkeleton from "../../components/ui/PageSkeleton";
import Button from "../../components/ui/Button";
import { ArrowBack } from "@mui/icons-material";

function CreatePage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const { data } = await salarySlipService.getEmployeesMeta();
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load employees for salary slip", err);
        toast.error("Unable to load employees");
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await salarySlipService.create(payload);
      toast.success("Salary slip created");
      const slipId = data?.id || data?._id;
      if (slipId) {
        navigate(`/salary-slip/${slipId}`);
      } else {
        console.warn("Created salary slip but no ID returned:", data);
        navigate("/salary-slip");
      }
    } catch (err) {
      console.error("Failed to create salary slip", err);
      const message =
        err?.response?.data?.error || "Failed to create salary slip";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate("/salary-slip")}
              className="flex items-center gap-2"
            >
              <ArrowBack className="w-5 h-5" />
              Back
            </Button>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">
            Create Salary Slip
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Create a new salary slip for an employee
          </p>
        </div>

        {loadingEmployees ? (
          <PageSkeleton type="form" />
        ) : employees.length === 0 ? (
          <div className="card text-center py-12">
            <p className="text-gray-500 text-lg">
              No employees available. Add employees before creating salary
              slips.
            </p>
          </div>
        ) : (
          <SalarySlipForm
            initialValues={makeEmptySalarySlipFormValues()}
            employees={employees}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/salary-slip")}
            submitLabel="Create Salary Slip"
            loading={submitting}
          />
        )}
      </motion.div>
    </div>
  );
}

export default CreatePage;
